import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const HowItWorksSection = () => {
  const [visible, setVisible] = useState([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          [0,1,2].forEach((i) => setTimeout(() => setVisible(v => [...v, i]), i * 150));
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const steps = [
    {
      id: 'goal',
      icon: 'Compass',
      title: 'Choose your goal',
      description: 'Manage emotions, connect to source, attract abundance — or all three. Your focus shapes everything.',
      color: 'from-violet-500/20 to-purple-500/20',
      iconColor: 'text-violet-400'
    },
    {
      id: 'voice',
      icon: 'Mic',
      title: 'Record your voice',
      description: 'Read 5 short affirmations aloud — 45–90 seconds. DepthMe clones your voice and uses it for all your guidance.',
      color: 'from-blue-500/20 to-cyan-500/20',
      iconColor: 'text-blue-500'
    },
    {
      id: 'ritual',
      icon: 'RefreshCw',
      title: 'Do your daily ritual',
      description: 'Listen to personalized guidance in your own voice. Repeat it. Journal your reflection. Build consistency over time.',
      color: 'from-emerald-500/20 to-green-500/20',
      iconColor: 'text-emerald-500'
    }
  ];

  return (
    <section id="how-it-works" ref={sectionRef} className="relative py-24 px-6 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <img src="/image1.jpg" alt="" aria-hidden="true" className="w-full h-full object-cover opacity-[0.12]" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-transparent to-background/30" />
      </div>
      <div className="absolute inset-0 cosmic-gradient opacity-15 pointer-events-none" />
      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            How DepthMe works
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Three steps to get started. Daily rituals that build on each other.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {steps?.map((step, index) => (
            <div
              key={step?.id}
              className={`glassmorphism p-8 rounded-3xl hover:scale-105 transition-all duration-500 group ${visible.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 120}ms` }}
            >
              {/* Step Number */}
              <div className="flex items-center justify-between mb-6">
                <div className="text-5xl font-bold text-primary/20">
                  {String(index + 1)?.padStart(2, '0')}
                </div>
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${step?.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                  <Icon name={step?.icon} size={28} className={step?.iconColor} />
                </div>
              </div>

              {/* Content */}
              <h3 className="text-2xl font-bold text-foreground mb-4">
                {step?.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {step?.description}
              </p>

            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;